import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  Optional,
  ViewChild,
} from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { WorkflowAddModalComponent } from '../workflow-add-modal/workflow-add-modal.component';
import { WorkflowEditModalComponent } from '../workflow-edit-modal/workflow-edit-modal.component';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Subject, takeUntil } from 'rxjs';
import { ClientAdminService } from '../client/client-admin.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { AppService } from '../../../app.service';

@Component({
    selector: 'gtw-client-save-modal',
    templateUrl: './client-save-modal.component.html',
    styleUrls: ['./client-save-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ClientSaveModalComponent implements OnInit, OnDestroy {
  @ViewChild('logoFileInput') logoFileInput!: ElementRef<HTMLInputElement>;

  title = 'New Client';
  mode: 'new' | 'edit' = 'new';
  minimise = false;
  saving = false;
  isClientNameDuplicate = false;
  currentClientKey = '';
  dbEnv = '';

  activeStep = 1;
  stepperData = [
    { stepNo: 1, stepTitle: 'General Information', enabled: true, isCurrent: true },
    { stepNo: 2, stepTitle: 'Application Setup', enabled: false, isCurrent: false },
    { stepNo: 3, stepTitle: 'Client Landing Details', enabled: false, isCurrent: false },
    { stepNo: 4, stepTitle: 'Workflow Setup', enabled: false, isCurrent: false },
  ];

  client: any = {
    client_name: '',
    client_desc: '',
    client_size: 'Large',
    client_active: '1',
    support_email: '',
    engagement_contact: [''],
    client_contact: '',
    client_key: '',
    client_datasource: '',
    client_gold_ds: '',
    eo_native_client_id: '',
    client_logo: '',
    client_logo_file_name: '',
    client_logo_change_success: false,
    client_css: 'Default',
    client_san: 'Default',
    database_options: 'Cloud',
    engagement_id: '',
  };

  supportEmail = '';
  clientDatasource = '';
  engagementContactsList: string[] = [''];
  tyEngagementIdsList: any[] = [{ taxYear: '', value: '' }];
  taxYears: any[] = [];
  sizeOptions = ['Large', 'Medium', 'Small'];

  logoPreviewUrl: SafeUrl | null = null;
  logoFile: File | null = null;
  dragOver = false;

  clientLandingDtls: any = {
    client_dtls_key: '',
    start_date: '',
    ext_filing_date: '',
    filing_date: '',
    est_filing_date: '',
    ext_filed: 'N',
    tax_return_filed_2018: 'N',
    business_sign_off: 'N',
  };

  defaultNodes: any[] = [];
  clientNodes: any[] = [];
  initialSetupData: any[] = [];
  setupLoading = false;
  showPreview = true;
  treeCollapsed = false;
  defaultChildShowFlags: Record<string, boolean> = {};
  clientChildShowFlags: Record<string, boolean> = {};

  domesticWorkflowTypes: any[] = [];
  foreignWorkflowTypes: any[] = [];
  selectedWFType = '';
  taxyearsMap: string[] = [];
  selectedTaxYear = '';
  scenariosByTaxyearArr: any = {};
  selectedScenario = '';
  workflowLoading = false;
  defaultWorkflowNodes: any[] = [];
  clientWorkflowNodes: any[] = [];
  defaultWfShowFlags: Record<string, boolean> = {};
  clientWfShowFlags: Record<string, boolean> = {};
  wfTreeCollapsed = false;
  showWfPreview = true;
  wfFilterLoaded = false;
  wfFilterFlag = false;
  initialClientWorkflowNodes: any[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    @Optional() public dialogRef: MatDialogRef<ClientSaveModalComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private clientAdminService: ClientAdminService,
    private appService: AppService,
    private gtwSnackbarService: GtwSnackbarService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
  ) {
    const userSettings = this.appService.USER_SETTINGS;
    if (userSettings?.user) {
      this.currentClientKey = String(userSettings.user.client_key ?? '');
    }

    this.loadTaxYears();

    const rowData = data?.rowData || {};
    if (rowData.client_key != null && rowData.client_key !== '') {
      this.mode = 'edit';
      this.title = 'Edit Client';
      this.client = { ...this.client, ...rowData };
      this.client.engagement_contact = Array.isArray(rowData.engagement_contact)
        ? rowData.engagement_contact
        : rowData.engagement_contact?.split(',') ?? [''];

      this.engagementContactsList = (this.client.engagement_contact || ['']).map((c: string) =>
        c && c.includes('@') ? c.substring(0, c.lastIndexOf('@')) : c
      );
      if (this.engagementContactsList.length === 0) this.engagementContactsList = [''];

      this.supportEmail = this.client.support_email && this.client.support_email.includes('@')
        ? this.client.support_email.substring(0, this.client.support_email.lastIndexOf('@'))
        : this.client.support_email || '';

      this.clientDatasource = this.client.client_datasource && this.client.client_datasource.length >= 7
        ? this.client.client_datasource.substring(3, 7)
        : '';

      if (rowData.client_env?.env_type) {
        this.dbEnv = rowData.client_env.env_type;
      }

      if (rowData.engagement_id) {
        this.tyEngagementIdsList = [];
        const parts = rowData.engagement_id.split(',');
        parts.forEach((p: string) => {
          const [taxYear, value] = p.split('=');
          this.tyEngagementIdsList.push({ taxYear: taxYear || '', value: value || '' });
        });
        if (this.tyEngagementIdsList.length === 0) {
          this.tyEngagementIdsList = [{ taxYear: '', value: '' }];
        }
      }

      this.loadClientLogo();
    }
  }

  ngOnInit(): void {
    this.clientAdminService.getEnvironmentType()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          const envData = resp?.data ?? resp;
          if (envData?.env_type) {
            this.dbEnv = envData.env_type;
          }
          this.cdr.detectChanges();
        },
        error: () => {},
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadTaxYears(): void {
    const appTaxYears = this.appService.TAX_YEARS;
    if (appTaxYears) {
      const firstJcdKey = Object.keys(appTaxYears)[0];
      if (firstJcdKey && Array.isArray(appTaxYears[firstJcdKey])) {
        this.taxYears = appTaxYears[firstJcdKey].map((ty: any) => ({
          label: ty.label || ty.value || ty,
          value: ty.value || ty.label || ty,
        }));
      }
    }
    if (this.taxYears.length === 0) {
      const current = new Date().getFullYear();
      for (let y = current; y >= current - 10; y--) {
        this.taxYears.push({ label: String(y), value: String(y) });
      }
    }
  }

  isLoggedInClient(): boolean {
    return String(this.client.client_key) === this.currentClientKey;
  }

  private hasClientKey(): boolean {
    const ck = this.client?.client_key;
    return ck != null && ck !== '';
  }

  setStep(step: number): void {
    if (step > 1 && !this.hasClientKey() && this.mode === 'new') {
      this.gtwSnackbarService.openSnackBar('Please complete and save General Information first.', 'gtw-snackbar--yellow');
      return;
    }

    this.stepperData.forEach(s => {
      s.isCurrent = false;
      s.enabled = s.stepNo <= step;
    });
    const clicked = this.stepperData.find(s => s.stepNo === step);
    if (clicked) clicked.isCurrent = true;
    this.activeStep = step;

    if (step === 2 && this.hasClientKey() && this.defaultNodes.length === 0) {
      this.loadApplicationSetup();
    } else if (step === 3 && this.hasClientKey()) {
      this.loadLandingDetails();
    } else if (step === 4 && this.hasClientKey()) {
      this.loadWorkflowData();
    }
    this.cdr.detectChanges();
  }

  checkClientName(): void {
    if (!this.client.client_name?.trim()) return;
    this.clientAdminService.checkClientName(this.client.client_name)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          const result = resp?.data ?? resp;
          this.isClientNameDuplicate = result === true || result === 'true';
          this.cdr.detectChanges();
        },
        error: () => {
          this.isClientNameDuplicate = false;
          this.cdr.detectChanges();
        },
      });
  }

  getDbEnvDisplay(): string {
    return this.dbEnv || '';
  }

  // ---- Logo handling ----

  onLogoDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = true;
  }

  onLogoDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;
  }

  onLogoDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleLogoFile(files[0]);
    }
  }

  onLogoFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleLogoFile(input.files[0]);
    }
  }

  triggerLogoUpload(): void {
    this.logoFileInput?.nativeElement?.click();
  }

  private handleLogoFile(file: File): void {
    if (!file.type.startsWith('image/')) {
      this.gtwSnackbarService.openSnackBar('Please select an image file.', 'gtw-snackbar--red');
      return;
    }
    this.logoFile = file;
    this.client.client_logo_file_name = file.name;
    this.client.client_logo_change_success = true;
    this.client.client_logo = 'pending_upload';
    const url = URL.createObjectURL(file);
    this.logoPreviewUrl = this.sanitizer.bypassSecurityTrustUrl(url);
    this.cdr.detectChanges();
  }

  removeLogo(): void {
    this.logoFile = null;
    this.logoPreviewUrl = null;
    this.client.client_logo = '';
    this.client.client_logo_file_name = '';
    this.client.client_logo_change_success = true;
    this.cdr.detectChanges();
  }

  private loadClientLogo(): void {
    if (!this.hasClientKey()) return;
    this.clientAdminService.downloadClientLogo(this.client.client_key)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob: Blob) => {
          if (blob && blob.size > 0) {
            const url = URL.createObjectURL(new Blob([blob], { type: 'image/jpeg' }));
            this.logoPreviewUrl = this.sanitizer.bypassSecurityTrustUrl(url);
            this.client.client_logo = 'existing';
            this.cdr.detectChanges();
          }
        },
        error: () => {},
      });
  }

  // ---- Engagement contacts ----

  addEngagementContact(): void {
    this.engagementContactsList.push('');
    this.cdr.detectChanges();
  }

  removeEngagementContact(index: number): void {
    this.engagementContactsList.splice(index, 1);
    this.cdr.detectChanges();
  }

  // ---- Engagement IDs ----

  addEngagementId(): void {
    this.tyEngagementIdsList.push({ taxYear: '', value: '' });
    this.cdr.detectChanges();
  }

  removeEngagementId(index: number): void {
    this.tyEngagementIdsList.splice(index, 1);
    this.cdr.detectChanges();
  }

  // ---- Step 2: Application Setup ----

  loadApplicationSetup(): void {
    if (!this.hasClientKey()) return;
    this.setupLoading = true;
    this.cdr.detectChanges();

    const defaultReq = { client_key: 0, jurisdiction_key: 1, user_id: 0 };
    const clientReq = { client_key: this.client.client_key, jurisdiction_key: 1, user_id: 0 };

    this.clientAdminService.getClientSetupData(defaultReq)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          const data = resp?.data ?? resp;
          if (data?.callSuccess === '1') {
            this.defaultNodes = (data.jsonObject || []).map((item: any) => ({
              ...item,
              is_selected: undefined,
              showChildren: true,
            }));
            this.computeNodeMeta(this.defaultNodes);
            this.initShowFlags(this.defaultNodes, this.defaultChildShowFlags);
          }
          this.loadClientNodes(clientReq);
        },
        error: () => {
          this.setupLoading = false;
          this.gtwSnackbarService.openSnackBar('Error loading default nav items.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  private loadClientNodes(reqData: any): void {
    this.clientAdminService.getClientSetupData(reqData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          this.setupLoading = false;
          const data = resp?.data ?? resp;
          if (data?.callSuccess === '1') {
            this.clientNodes = (data.jsonObject || []).map((item: any) => ({
              ...item,
              showChildren: true,
            }));
            this.computeNodeMeta(this.clientNodes);
            this.initialSetupData = JSON.parse(JSON.stringify(this.clientNodes));
            this.initShowFlags(this.clientNodes, this.clientChildShowFlags);
            this.copySelectionsToDefault();
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.setupLoading = false;
          this.gtwSnackbarService.openSnackBar('Error loading application setup.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  private initShowFlags(nodes: any[], flags: Record<string, boolean>): void {
    nodes.forEach(n => {
      flags['show_' + n.parent_id] = true;
    });
  }

  private copySelectionsToDefault(): void {
    this.clientNodes.forEach(clientItem => {
      const defaultNode = this.defaultNodes.find(
        d => d.child_id === clientItem.child_id && d.nav_level == clientItem.nav_level
      );
      if (defaultNode) {
        defaultNode.is_selected = clientItem.is_selected;
      }
    });
  }

  private computeNodeMeta(nodes: any[]): void {
    const parentIds = new Set(nodes.map(n => n.parent_id));
    nodes.forEach(n => {
      n._hasChildren = parentIds.has(n.child_id);
      n._paddingLeft = (parseInt(n.nav_level, 10) || 1) * 20;
    });
  }

  toggleNodeCollapse(node: any, list: any[], flags: Record<string, boolean>): void {
    node.showChildren = !node.showChildren;
    this.collapseChildrenRecursive(node, node, list, flags);
    this.cdr.detectChanges();
  }

  private collapseChildrenRecursive(node: any, rootNode: any, list: any[], flags: Record<string, boolean>): void {
    flags['show_' + node.child_id] = rootNode.showChildren;
    const children = list.filter(n => n.parent_id === node.child_id);
    children.forEach(child => {
      child.showChildren = rootNode.showChildren;
      this.collapseChildrenRecursive(child, rootNode, list, flags);
    });
  }

  onPreviewToggle(event: any): void {
    this.showPreview = !this.showPreview;
    this.cdr.detectChanges();
  }

  toggleCollapseAll(): void {
    this.treeCollapsed = !this.treeCollapsed;
    this.defaultNodes.forEach(n => {
      n.showChildren = !this.treeCollapsed;
      if (n.nav_level !== '1') {
        this.defaultChildShowFlags['show_' + n.parent_id] = !this.treeCollapsed;
      }
    });
    this.clientNodes.forEach(n => {
      n.showChildren = !this.treeCollapsed;
      if (n.nav_level !== '1') {
        this.clientChildShowFlags['show_' + n.parent_id] = !this.treeCollapsed;
      }
    });
    this.cdr.detectChanges();
  }

  onDefaultNodeClick(node: any): void {
    node.is_selected = (node.is_selected === 2 || node.is_selected === 1) ? 0 : 2;
    this.checkAllChildren(node, this.defaultNodes);
    this.checkParent(node, this.defaultNodes);
    this.copyNodeDefaultToClient(node);
    this.cdr.detectChanges();
  }

  private copyNodeDefaultToClient(node: any): void {
    const elem = this.clientNodes.find(
      c => c.child_id === node.child_id && c.nav_level === node.nav_level
    );
    if (elem) {
      elem.is_selected = node.is_selected;
      if (elem.type === 'category') {
        this.clientNodes
          .filter(c => c.parent_id === elem.child_id && c.is_custom === '1')
          .forEach(c => c.is_selected = elem.is_selected);
      }
    }
    const children = this.defaultNodes.filter(n => n.parent_id === node.child_id);
    children.forEach(child => this.copyNodeDefaultToClient(child));
  }

  copyDefaultTree(): void {
    const allSelected = this.defaultNodes.every(n => n.is_selected === 2 || n.is_selected === undefined);
    const newVal = allSelected ? 0 : 2;
    this.defaultNodes.forEach(n => {
      n.is_selected = newVal;
      const clientNode = this.clientNodes.find(
        c => c.child_id === n.child_id && c.nav_level === n.nav_level
      );
      if (clientNode) clientNode.is_selected = newVal;
    });
    this.cdr.detectChanges();
  }

  private checkAllChildren(node: any, list: any[]): void {
    const children = list.filter(n => n.parent_id === node.child_id);
    children.forEach(child => {
      child.is_selected = node.is_selected;
      this.checkAllChildren(child, list);
    });
  }

  private checkParent(node: any, list: any[]): void {
    if (!node.parent_id) return;
    const parent = list.find(n => n.child_id === node.parent_id);
    if (!parent) return;
    const siblings = list.filter(n => n.parent_id === parent.child_id);
    const selected = siblings.filter(n => n.is_selected !== 0 && n.is_selected !== undefined);
    const intermittent = selected.filter(n => n.is_selected === 1);

    if (selected.length === siblings.length && intermittent.length === 0) {
      parent.is_selected = 2;
    } else if (selected.length > 0) {
      parent.is_selected = 1;
    } else {
      parent.is_selected = 0;
    }
    this.checkParent(parent, list);
  }

  startNodeEdit(node: any): void {
    node._editing = true;
    node._retainLabel = node.name;
    this.cdr.detectChanges();
  }

  saveNodeEdit(node: any): void {
    node._editing = false;
    delete node._retainLabel;
    this.cdr.detectChanges();
  }

  cancelNodeEdit(node: any): void {
    node.name = node._retainLabel || node.name;
    node._editing = false;
    delete node._retainLabel;
    this.cdr.detectChanges();
  }

  saveAppSetup(): void {
    if (!this.hasClientKey()) return;
    this.clientAdminService.saveAppSetup(this.client.client_key, this.clientNodes)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          const data = resp?.data ?? resp;
          if (data?.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar('Application setup saved.', 'gtw-snackbar--green');
            this.initialSetupData = JSON.parse(JSON.stringify(this.clientNodes));
          } else {
            this.gtwSnackbarService.openSnackBar('Error saving application setup.', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.gtwSnackbarService.openSnackBar('Error saving application setup.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  // ---- Step 3: Client Landing Details ----

  loadLandingDetails(): void {
    if (!this.hasClientKey()) return;
    this.clientAdminService.getClientLandingDetails(this.client.client_key)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          const data = resp?.data ?? resp;
          if (data?.callSuccess === '1' && data.clientLanDtls?.length > 0) {
            this.clientLandingDtls = { ...this.clientLandingDtls, ...data.clientLanDtls[0] };
          } else if (data?.clientLanDtls?.length > 0) {
            this.clientLandingDtls = { ...this.clientLandingDtls, ...data.clientLanDtls[0] };
          }
          this.cdr.detectChanges();
        },
        error: (err: any) => {
          console.error('getClientLandDtlsByKey error:', err);
          this.gtwSnackbarService.openSnackBar('Error loading landing details.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  // ---- Step 4: Workflow Setup ----

  loadWorkflowData(): void {
    if (!this.hasClientKey() || this.wfFilterLoaded) return;
    this.workflowLoading = true;
    this.cdr.detectChanges();

    this.clientAdminService.getWorkflowTypes(this.client.client_key)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          this.workflowLoading = false;
          const data = resp?.data ?? resp;
          const types = data?.workflowType || [];
          this.domesticWorkflowTypes = types.filter((t: any) => t.wfStep_Type === 'D');
          this.foreignWorkflowTypes = types.filter((t: any) => t.wfStep_Type === 'F');
          this.wfFilterLoaded = true;
          this.cdr.detectChanges();
        },
        error: () => {
          this.workflowLoading = false;
          this.cdr.detectChanges();
        },
      });

    this.clientAdminService.getTaxYearAndScenarioFilters(this.client.client_key)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          const data = resp?.data ?? resp;
          this.scenariosByTaxyearArr = data?.ScenariosByTaxyearMap || {};
          this.taxyearsMap = data?.taxyearsMap || Object.keys(this.scenariosByTaxyearArr);
          this.cdr.detectChanges();
        },
        error: () => {},
      });
  }

  get currentScenarios(): any[] {
    return this.scenariosByTaxyearArr[this.selectedTaxYear] || [];
  }

  refreshWorkflowTree(): void {
    if (!this.selectedWFType || !this.selectedTaxYear || !this.selectedScenario) return;
    this.workflowLoading = true;
    this.wfFilterFlag = false;
    this.defaultWorkflowNodes = [];
    this.clientWorkflowNodes = [];
    this.cdr.detectChanges();

    const ck = this.client.client_key;
    if (ck === '0' || ck === '999') {
      this.loadOnlyDefaultWorkflowTree();
    } else {
      this.loadDefaultAndClientWorkflowTree();
    }
  }

  private loadOnlyDefaultWorkflowTree(): void {
    this.clientAdminService.getOnlyDefaultWorkflowTree(
      this.selectedWFType, this.selectedTaxYear, this.selectedScenario, this.client.client_key,
    ).pipe(takeUntil(this.destroy$)).subscribe({
      next: (resp: any) => {
        this.workflowLoading = false;
        const data = resp?.data ?? resp;
        if (data?.errorMessage) {
          this.gtwSnackbarService.openSnackBar(data.errorMessage, 'gtw-snackbar--red');
        } else {
          const nodes = (data?.workflowHierarchy || []).map((item: any) => ({
            ...item,
            showChildren: true,
          }));
          this.computeNodeMeta(nodes);
          this.defaultWorkflowNodes = nodes;
          this.clientWorkflowNodes = nodes;
          this.initShowFlags(this.defaultWorkflowNodes, this.defaultWfShowFlags);
          this.selectActiveNodesInDefault(this.clientWorkflowNodes);
        }
        this.convertTaxType(this.selectedWFType);
        this.wfFilterFlag = true;
        this.cdr.detectChanges();
      },
      error: () => {
        this.workflowLoading = false;
        this.gtwSnackbarService.openSnackBar('Error loading workflow tree.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      },
    });
  }

  private selectActiveNodesInDefault(nodes: any[]): void {
    nodes.forEach(n => {
      if (n.is_selected === 2) {
        const def = this.defaultWorkflowNodes.find(
          d => d.child_id === n.child_id && d.nav_level === n.nav_level
        );
        if (def) def.is_selected = 2;
      }
    });
  }

  private loadDefaultAndClientWorkflowTree(): void {
    this.clientAdminService.getWorkflowTree(this.selectedWFType, this.selectedTaxYear, this.selectedScenario, '0')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          const data = resp?.data ?? resp;
          if (data?.errorMessage) {
            this.workflowLoading = false;
            this.gtwSnackbarService.openSnackBar(data.errorMessage, 'gtw-snackbar--red');
            this.cdr.detectChanges();
          } else {
            this.defaultWorkflowNodes = (data?.workflowHierarchy || []).map((item: any) => ({
              ...item, showChildren: true,
            }));
            this.computeNodeMeta(this.defaultWorkflowNodes);
            this.initShowFlags(this.defaultWorkflowNodes, this.defaultWfShowFlags);
            this.loadClientWorkflowTree();
          }
        },
        error: () => {
          this.workflowLoading = false;
          this.gtwSnackbarService.openSnackBar('Error loading default workflow.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  private loadClientWorkflowTree(): void {
    this.clientAdminService.getWorkflowTree(this.selectedWFType, this.selectedTaxYear, this.selectedScenario, this.client.client_key)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          this.workflowLoading = false;
          const data = resp?.data ?? resp;
          if (data?.errorMessage) {
            this.gtwSnackbarService.openSnackBar(data.errorMessage, 'gtw-snackbar--red');
          } else {
            this.clientWorkflowNodes = (data?.workflowHierarchy || []).map((item: any) => ({
              ...item,
              showChildren: true,
            }));
            this.initialClientWorkflowNodes = JSON.parse(JSON.stringify(this.clientWorkflowNodes));
            this.computeNodeMeta(this.clientWorkflowNodes);
            this.initShowFlags(this.clientWorkflowNodes, this.clientWfShowFlags);
            if (this.defaultWorkflowNodes.length > 0 && this.clientWorkflowNodes.length > 0) {
              this.copyWfSelectionsToDefault();
              this.client.wfst_key = this.selectedWFType;
            }
          }
          this.convertTaxType(this.selectedWFType);
          this.wfFilterFlag = true;
          this.cdr.detectChanges();
        },
        error: () => {
          this.workflowLoading = false;
          this.gtwSnackbarService.openSnackBar('Error loading client workflow.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  private convertTaxType(wfstKey: string): void {
    const map: Record<string, string> = {
      '1': 'Domestic Corporation', '2': 'Domestic Partnership', '3': 'Domestic Insurance',
      '4': 'Foreign Corporation', '5': 'Foreign Partnership', '6': 'Foreign Insurance',
    };
    this.client.tax_type = map[wfstKey] || '';
  }

  private copyWfSelectionsToDefault(): void {
    this.clientWorkflowNodes.forEach(clientItem => {
      const defaultNode = this.defaultWorkflowNodes.find(
        d => d.child_id === clientItem.child_id && d.nav_level == clientItem.nav_level
      );
      if (defaultNode) {
        defaultNode.is_selected = clientItem.is_selected;
        defaultNode.has_new = clientItem.has_new;
        if (defaultNode.has_new === '1' && defaultNode.is_selected === 2) {
          defaultNode.showPushBtn = true;
        }
      }
    });
  }

  wfSelectedAll = true;

  copyDefaultWfTree(): void {
    this.wfSelectedAll = !this.wfSelectedAll;
    const val = this.wfSelectedAll ? 0 : 2;
    this.defaultWorkflowNodes.forEach(n => n.is_selected = val);
    this.cdr.detectChanges();
  }

  onDefaultWfNodeClick(node: any): void {
    node.is_selected = (node.is_selected === 2 || node.is_selected === 1) ? 0 : 2;
    this.checkAllChildren(node, this.defaultWorkflowNodes);
    this.checkParent(node, this.defaultWorkflowNodes);
    this.copyWfNodeDefaultToClient(node);
    this.cdr.detectChanges();
  }

  private copyWfNodeDefaultToClient(node: any): void {
    const elem = this.clientWorkflowNodes.find(
      c => c.child_id === node.child_id && c.nav_level === node.nav_level
    );
    if (elem) {
      if (elem.client_key === '0') {
        elem.is_selected = node.is_selected;
        elem.is_active = node.is_selected === 0 ? '0' : '1';
      } else {
        elem.is_selected = node.is_selected;
        if (elem.type === 'category') {
          this.clientWorkflowNodes
            .filter(c => c.parent_id === elem.child_id && c.is_custom === '1')
            .forEach(c => c.is_selected = elem.is_selected);
        }
      }
    }
    this.checkWfCustomNodes(node);
  }

  private checkWfCustomNodes(node: any): void {
    const children = this.clientWorkflowNodes.filter(
      c => c.parent_id === node.child_id && c.is_custom === '1'
    );
    children.forEach(child => {
      child.is_selected = node.is_selected;
      this.checkWfCustomNodes(child);
    });
  }

  toggleWfCollapseAll(): void {
    this.wfTreeCollapsed = !this.wfTreeCollapsed;
    [this.defaultWorkflowNodes, this.clientWorkflowNodes].forEach(nodes => {
      const flags = nodes === this.defaultWorkflowNodes ? this.defaultWfShowFlags : this.clientWfShowFlags;
      nodes.forEach(n => {
        n.showChildren = !this.wfTreeCollapsed;
        if (n.nav_level !== '1') {
          flags['show_' + n.parent_id] = !this.wfTreeCollapsed;
        }
      });
    });
    this.cdr.detectChanges();
  }

  onWfPreviewToggle(event: any): void {
    this.showWfPreview = !this.showWfPreview;
    this.cdr.detectChanges();
  }

  openAddCategory(): void {
    this.openWorkflowAddModal('Category');
  }

  openAddStep(): void {
    this.openWorkflowAddModal('Step');
  }

  private openWorkflowAddModal(origin: 'Category' | 'Step'): void {
    const config = new MatDialogConfig();
    config.panelClass = ['admin-modal-panel', 'admin-child-modal-panel'];
    config.width = '600px';
    config.disableClose = true;
    config.data = {
      origin,
      defaultTreeData: this.defaultWorkflowNodes,
      clientTreeData: this.clientWorkflowNodes,
      clientKey: this.client.client_key,
      wfstKey: this.selectedWFType,
      taxYear: this.selectedTaxYear,
      scenario: this.selectedScenario,
    };
    const ref = this.dialog.open(WorkflowAddModalComponent, config);
    ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result?.saved) {
        this.refreshWorkflowTree();
      }
    });
  }

  openEditWorkflow(node: any): void {
    const pType = node.type || '';
    const wfscompKey = pType === 'component'
      ? (node.child_id || '').replace('$$', '')
      : (node.child_id || '').replace('##', '');

    this.clientAdminService.getWorkflowComponent(
      pType, node.client_key, wfscompKey, node.tax_year, node.scenario,
    ).pipe(takeUntil(this.destroy$)).subscribe({
      next: (resp: any) => {
        const data = resp?.data ?? resp;
        const compList = data?.workflowcomponentList;
        if (!compList || compList.length === 0) {
          this.gtwSnackbarService.openSnackBar('No component data found.', 'gtw-snackbar--yellow');
          return;
        }
        this.clientAdminService.getAllVersionNumbers(
          pType, node.client_key, wfscompKey, node.tax_year, node.scenario,
        ).pipe(takeUntil(this.destroy$)).subscribe({
          next: (verResp: any) => {
            const verData = verResp?.data ?? verResp;
            const versionNumList = verData?.versionNumList || [];
            this.openEditWorkflowModal(node, compList, versionNumList);
          },
          error: () => {
            this.openEditWorkflowModal(node, compList, []);
          },
        });
      },
      error: () => {
        this.gtwSnackbarService.openSnackBar('Error loading workflow component.', 'gtw-snackbar--red');
      },
    });
  }

  private openEditWorkflowModal(node: any, compList: any[], versionNumList: any[]): void {
    node._editing = false;
    const config = new MatDialogConfig();
    config.panelClass = ['admin-modal-panel', 'admin-child-modal-panel'];
    config.width = '700px';
    config.disableClose = true;
    config.data = { node, compList, versionNumList };
    const ref = this.dialog.open(WorkflowEditModalComponent, config);
    ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result?.saved) {
        this.refreshWorkflowTree();
      }
    });
  }

  getWorkflowType(node: any): string {
    const t = node.type || '';
    return t.charAt(0).toUpperCase() + t.substring(1);
  }

  saveWorkflow(): void {
    if (!this.hasClientKey() || !this.selectedWFType || !this.selectedTaxYear || !this.selectedScenario) {
      this.gtwSnackbarService.openSnackBar('Please load workflow tree first.', 'gtw-snackbar--yellow');
      return;
    }
    if (this.clientWorkflowNodes.length === 0) {
      this.gtwSnackbarService.openSnackBar('No workflow data to save.', 'gtw-snackbar--yellow');
      return;
    }

    const changes = this.clientWorkflowNodes.filter(item => {
      return this.initialClientWorkflowNodes.find(item2 =>
        item2.child_id === item.child_id &&
        item2.nav_level === item.nav_level &&
        (item2.is_selected !== item.is_selected || item2.name !== item.name || item2.priority !== item.priority)
      );
    });

    if (changes.length === 0) {
      this.gtwSnackbarService.openSnackBar('No changes to save.', 'gtw-snackbar--yellow');
      return;
    }

    this.saving = true;
    this.cdr.detectChanges();
    this.clientAdminService.saveClientWorkflow(
      this.selectedWFType, this.selectedTaxYear, this.selectedScenario,
      this.client.client_key, changes,
    ).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          this.saving = false;
          const data = resp?.data ?? resp;
          if (data?.result?.value > 0) {
            this.gtwSnackbarService.openSnackBar(
              data.result.message || 'Workflow saved successfully.', 'gtw-snackbar--green'
            );
            this.refreshWorkflowTree();
          } else {
            this.gtwSnackbarService.openSnackBar(
              data?.result?.message || 'Error saving workflow.', 'gtw-snackbar--red'
            );
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.saving = false;
          this.gtwSnackbarService.openSnackBar('Error saving workflow.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  // ---- Save ----

  save(): void {
    if (this.activeStep === 2) { this.saveAppSetup(); return; }
    if (this.activeStep === 3) { this.saveLandingDetails(); return; }
    if (this.activeStep === 4) { this.saveWorkflow(); return; }
    if (!this.client.client_name?.trim()) {
      this.gtwSnackbarService.openSnackBar('Client Name is required.', 'gtw-snackbar--red');
      return;
    }
    if (!this.client.client_desc?.trim()) {
      this.gtwSnackbarService.openSnackBar('Client Description is required.', 'gtw-snackbar--red');
      return;
    }
    if (this.isClientNameDuplicate) {
      this.gtwSnackbarService.openSnackBar('Client Name already exists.', 'gtw-snackbar--red');
      return;
    }
    if (!this.clientDatasource && this.mode === 'new') {
      this.gtwSnackbarService.openSnackBar('Client Datasource is required.', 'gtw-snackbar--red');
      return;
    }
    const contactsValid = this.engagementContactsList.filter(c => c.trim() !== '');
    if (contactsValid.length === 0) {
      this.gtwSnackbarService.openSnackBar('At least one Engagement Contact is required.', 'gtw-snackbar--red');
      return;
    }

    this.saving = true;
    this.cdr.detectChanges();

    const payload = { ...this.client };
    payload.client_size = payload.client_size || 'Large';
    payload.client_active = payload.client_active || '1';
    payload.support_email = this.supportEmail ? this.supportEmail + '@servicedesk.com' : '';
    payload.engagement_contact = this.engagementContactsList
      .filter(c => c.trim() !== '')
      .map(c => c.includes('@') ? c : c + '@pwc.com')
      .join(',');
    payload.client_datasource = this.mode === 'new'
      ? 'GTW' + this.clientDatasource.toUpperCase() + this.getDbEnvDisplay()
      : this.client.client_datasource;

    const engIds = this.tyEngagementIdsList
      .filter(e => e.taxYear && e.value)
      .map(e => e.taxYear + '=' + e.value);
    payload.engagement_id = engIds.join(',');

    payload.client_css = 'Default';
    payload.client_san = 'Default';
    payload.database_options = 'Cloud';

    this.clientAdminService.saveClient(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.saving = false;
          const result = response?.data ?? response;
          const success = result?.callSuccess;
          if (parseInt(success, 10) > 0) {
            this.gtwSnackbarService.openSnackBar('Client saved successfully.', 'gtw-snackbar--green');
            this.dialogRef?.close('saved');
          } else {
            this.gtwSnackbarService.openSnackBar(
              'Problem saving client: ' + (result?.errorMessage ?? ''),
              'gtw-snackbar--red',
            );
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.saving = false;
          this.gtwSnackbarService.openSnackBar('Problem saving client.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  saveLandingDetails(): void {
    if (!this.hasClientKey()) return;
    const payload = {
      ...this.clientLandingDtls,
      client_key: this.client.client_key,
      number_of_users: '1',
      hour_spent: '1',
      percentile_complete: '1',
      active_users: '1',
      engagmnt_contact_person: 'contact@pwc.com',
      engagmnt_contact_number: '000-000-0000',
    };

    this.clientAdminService.saveClientLandingDetails(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          const data = resp?.data ?? resp;
          if (data?.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar('Client Landing Details saved successfully.', 'gtw-snackbar--green');
          } else {
            this.gtwSnackbarService.openSnackBar('Error saving landing details.', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.gtwSnackbarService.openSnackBar('Error saving landing details.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  removeClient(): void {
    if (!this.hasClientKey()) return;
    this.clientAdminService.deleteClient(this.client.client_key)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          const data = resp?.data ?? resp;
          if (String(data?.callSuccess) === '1') {
            this.gtwSnackbarService.openSnackBar('Client removed successfully.', 'gtw-snackbar--green');
            this.dialogRef?.close('saved');
          } else {
            this.gtwSnackbarService.openSnackBar(data?.errorMessage || 'Problem removing client.', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.gtwSnackbarService.openSnackBar('Problem removing client.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  cancel(): void {
    this.dialogRef?.close();
  }

  clearForm(): void {
    const keepKey = this.mode === 'edit' ? this.client.client_key : '';
    this.client = {
      client_name: '', client_desc: '', client_size: 'Large', client_active: '1',
      support_email: '', engagement_contact: [''], client_contact: '', client_key: keepKey,
      client_datasource: '', client_gold_ds: '', eo_native_client_id: '',
      client_logo: '', client_logo_file_name: '', client_logo_change_success: false,
      client_css: 'Default', client_san: 'Default', database_options: 'Cloud', engagement_id: '',
    };
    this.supportEmail = '';
    this.clientDatasource = '';
    this.engagementContactsList = [''];
    this.tyEngagementIdsList = [{ taxYear: '', value: '' }];
    this.logoPreviewUrl = null;
    this.logoFile = null;
    this.cdr.detectChanges();
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackByChildId(index: number, item: any): string {
    return item.child_id;
  }
}
